import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FaSync, FaToggleOn, FaToggleOff, FaPlay, FaHistory, FaFileInvoiceDollar, FaBoxOpen, FaSitemap } from 'react-icons/fa';
import CatalogExportCard from './CatalogExportCard';

const MODELS = [
  { key: 'TallyQuotation', label: 'Tally Quotations' },
  { key: 'QuotationNew', label: 'Quotations' },
  { key: 'ActivityLogNew', label: 'Activity Logs' },
  { key: 'DeliveryOrder', label: 'Delivery Orders' },
];

const API = process.env.REACT_APP_API_BASE_URL;

const DataExport = () => {
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');

  // ── Manual export state ──
  const [mongoUrl, setMongoUrl] = useState('');
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [connectionMessage, setConnectionMessage] = useState('');
  const [selectedModels, setSelectedModels] = useState(MODELS.map(m => m.key));
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [exporting, setExporting] = useState(false);
  const [results, setResults] = useState(null);

  // ── Auto sync state ──
  const [syncConfig, setSyncConfig] = useState(null);
  const [syncUrl, setSyncUrl] = useState('');
  const [syncModels, setSyncModels] = useState(MODELS.map(m => m.key));
  const [syncTestStatus, setSyncTestStatus] = useState(null);
  const [syncTestMessage, setSyncTestMessage] = useState('');
  const [savingSyncConfig, setSavingSyncConfig] = useState(false);
  const [runningNow, setRunningNow] = useState(false);
  const [syncLogs, setSyncLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);
  const [loadingLogs, setLoadingLogs] = useState(false);

  // ── Tally voucher daily sync state ──
  const [voucherSyncTime, setVoucherSyncTime] = useState('');
  const [voucherEnabled, setVoucherEnabled] = useState(false);
  const [savingVoucherConfig, setSavingVoucherConfig] = useState(false);
  const [runningVoucherNow, setRunningVoucherNow] = useState(false);
  const [voucherLogs, setVoucherLogs] = useState([]);
  const [showVoucherLogs, setShowVoucherLogs] = useState(false);
  const [loadingVoucherLogs, setLoadingVoucherLogs] = useState(false);

  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'darkMode') setDarkMode(e.newValue === 'true');
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const fetchSyncConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/super-admin/sync-config`);
      if (res.data.config) {
        setSyncConfig(res.data.config);
        setSyncUrl(res.data.config.mongoUrl);
        setSyncModels(res.data.config.models);
        setVoucherSyncTime(res.data.config.voucherSyncTime || '');
        setVoucherEnabled(res.data.config.voucherEnabled === true);
      }
    } catch (_) {}
  }, []);

  useEffect(() => { fetchSyncConfig(); }, [fetchSyncConfig]);

  // ── Style helpers ──
  const cardBg = darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white';
  const text = darkMode ? 'text-gray-100' : 'text-gray-900';
  const textMuted = darkMode ? 'text-gray-400' : 'text-gray-500';
  const textLabel = darkMode ? 'text-gray-300' : 'text-gray-700';
  const inputCls = darkMode
    ? 'bg-gray-700 text-gray-100 border-gray-600 placeholder-gray-500 focus:ring-blue-400'
    : 'bg-white text-gray-900 border-gray-300 placeholder-gray-400 focus:ring-blue-500';
  const theadBg = darkMode ? 'bg-gray-700' : 'bg-gray-50';
  const theadText = darkMode ? 'text-gray-300' : 'text-gray-500';
  const tbodyBg = darkMode ? 'bg-gray-800' : 'bg-white';
  const tbodyDivide = darkMode ? 'divide-gray-700' : 'divide-gray-200';
  const tbodyText = darkMode ? 'text-gray-200' : 'text-gray-800';
  const tbodyMuted = darkMode ? 'text-gray-400' : 'text-gray-600';
  const divider = darkMode ? 'border-gray-700' : 'border-gray-200';

  // ── Manual export handlers ──
  const handleModelToggle = (key) => setSelectedModels(prev =>
    prev.includes(key) ? prev.filter(m => m !== key) : [...prev, key]
  );

  const handleTestConnection = async () => {
    if (!mongoUrl.trim()) { toast.error('Please enter a MongoDB URL'); return; }
    setConnectionStatus('testing'); setConnectionMessage('');
    try {
      const res = await axios.post(`${API}/super-admin/test-external-db`, { mongoUrl: mongoUrl.trim() });
      setConnectionStatus('success'); setConnectionMessage(res.data.message || 'Connection successful');
    } catch (err) {
      setConnectionStatus('error'); setConnectionMessage(err.response?.data?.message || 'Connection failed');
    }
  };

  const handleExport = async () => {
    if (connectionStatus !== 'success') { toast.error('Please test the connection successfully before exporting'); return; }
    if (selectedModels.length === 0) { toast.error('Please select at least one model'); return; }
    setExporting(true); setResults(null);
    try {
      const res = await axios.post(`${API}/super-admin/export-to-external-db`, {
        mongoUrl: mongoUrl.trim(), models: selectedModels,
        dateRange: (dateRange.from || dateRange.to) ? dateRange : undefined,
      }, { timeout: 300000 });
      setResults(res.data.results); toast.success('Export completed');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Export failed');
    } finally { setExporting(false); }
  };

  const connectionBadge = () => {
    if (connectionStatus === 'testing') return <span className="ml-2 text-sm text-yellow-500 font-medium">Testing...</span>;
    if (connectionStatus === 'success') return <span className="ml-2 text-sm text-green-500 font-medium">✓ {connectionMessage}</span>;
    if (connectionStatus === 'error') return <span className="ml-2 text-sm text-red-500 font-medium">✗ {connectionMessage}</span>;
    return null;
  };

  // ── Auto sync handlers ──
  const handleSyncModelToggle = (key) => setSyncModels(prev =>
    prev.includes(key) ? prev.filter(m => m !== key) : [...prev, key]
  );

  const handleTestSyncConnection = async () => {
    if (!syncUrl.trim()) { toast.error('Please enter a MongoDB URL'); return; }
    setSyncTestStatus('testing'); setSyncTestMessage('');
    try {
      const res = await axios.post(`${API}/super-admin/test-external-db`, { mongoUrl: syncUrl.trim() });
      setSyncTestStatus('success'); setSyncTestMessage(res.data.message || 'Connection successful');
    } catch (err) {
      setSyncTestStatus('error'); setSyncTestMessage(err.response?.data?.message || 'Connection failed');
    }
  };

  const handleSaveSyncConfig = async () => {
    if (!syncUrl.trim()) { toast.error('Please enter a MongoDB URL'); return; }
    if (syncModels.length === 0) { toast.error('Please select at least one collection'); return; }
    setSavingSyncConfig(true);
    try {
      const res = await axios.post(`${API}/super-admin/sync-config`, {
        mongoUrl: syncUrl.trim(), models: syncModels,
      });
      setSyncConfig(res.data.config);
      toast.success('Auto sync config saved. Cron will run every 15 minutes.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save config');
    } finally { setSavingSyncConfig(false); }
  };

  const handleToggleSync = async () => {
    try {
      const res = await axios.patch(`${API}/super-admin/sync-config/toggle`);
      setSyncConfig(prev => ({ ...prev, enabled: res.data.enabled }));
      toast.success(res.data.enabled ? 'Auto sync enabled' : 'Auto sync paused');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Toggle failed');
    }
  };

  const handleRunNow = async () => {
    setRunningNow(true);
    try {
      await axios.post(`${API}/super-admin/sync-config/run-now`);
      toast.success('Sync started in background — check logs in a moment');
      setTimeout(() => { fetchSyncConfig(); if (showLogs) loadSyncLogs(); }, 5000);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to start sync');
    } finally { setRunningNow(false); }
  };

  const loadSyncLogs = async () => {
    setLoadingLogs(true);
    try {
      const res = await axios.get(`${API}/super-admin/sync-config/logs`);
      setSyncLogs(res.data.logs || []);
    } catch (_) {} finally { setLoadingLogs(false); }
  };

  const handleShowLogs = () => {
    if (!showLogs) loadSyncLogs();
    setShowLogs(v => !v);
  };

  const syncStatusBadge = () => {
    if (!syncConfig) return null;
    if (syncConfig.lastRunStatus === 'never') return <span className={`text-xs ${textMuted}`}>Never run</span>;
    return (
      <span className={`text-xs font-medium ${syncConfig.lastRunStatus === 'success' ? 'text-green-500' : 'text-red-500'}`}>
        Last: {syncConfig.lastRunStatus === 'success' ? '✓' : '✗'} {syncConfig.lastRunAt ? new Date(syncConfig.lastRunAt).toLocaleString() : ''}
      </span>
    );
  };

  // ── Tally voucher daily sync handlers ──
  const handleSaveVoucherConfig = async () => {
    if (!voucherSyncTime) { toast.error('Please select a daily sync time'); return; }
    if (!syncConfig?.mongoUrl) { toast.error('Please save the Auto Sync config (MongoDB URL) first'); return; }
    setSavingVoucherConfig(true);
    try {
      const res = await axios.post(`${API}/super-admin/sync-config`, { voucherSyncTime });
      setSyncConfig(res.data.config);
      toast.success(`Voucher sync time saved. Runs daily at ${voucherSyncTime}.`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save voucher config');
    } finally { setSavingVoucherConfig(false); }
  };

  const handleToggleVoucherSync = async () => {
    try {
      const res = await axios.patch(`${API}/super-admin/sync-config/voucher-toggle`);
      const enabled = res.data.enabled;
      setVoucherEnabled(enabled);
      setSyncConfig(prev => prev ? { ...prev, voucherEnabled: enabled } : prev);
      toast.success(enabled ? 'Tally voucher daily sync enabled' : 'Tally voucher daily sync paused');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Toggle failed');
    }
  };

  const handleVoucherRunNow = async (full = false) => {
    setRunningVoucherNow(true);
    try {
      await axios.post(`${API}/super-admin/sync-config/voucher-run-now`, { full });
      toast.success((full ? 'Full' : 'Incremental') + ' voucher export started — status updates while it runs');
      waitForVoucherRun();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to start voucher export');
    } finally { setRunningVoucherNow(false); }
  };

  // Poll the config while a voucher export is in progress so the status
  // badge and logs update live instead of after a fixed delay.
  const waitForVoucherRun = () => {
    let attempts = 0;
    const timer = setInterval(async () => {
      attempts += 1;
      try {
        const res = await axios.get(`${API}/super-admin/sync-config`);
        if (res.data.config) {
          setSyncConfig(res.data.config);
          setVoucherSyncTime(res.data.config.voucherSyncTime || '');
          setVoucherEnabled(res.data.config.voucherEnabled === true);
          if (showVoucherLogs) loadVoucherLogs();
          if (!res.data.config.voucherRunning || attempts >= 200) {
            clearInterval(timer);
          }
        }
      } catch (_) {
        if (attempts >= 200) clearInterval(timer);
      }
    }, 3000);
  };

  const loadVoucherLogs = async () => {
    setLoadingVoucherLogs(true);
    try {
      const res = await axios.get(`${API}/super-admin/sync-config/voucher-logs`);
      setVoucherLogs(res.data.logs || []);
    } catch (_) {} finally { setLoadingVoucherLogs(false); }
  };

  const handleShowVoucherLogs = () => {
    if (!showVoucherLogs) loadVoucherLogs();
    setShowVoucherLogs(v => !v);
  };

  const voucherStatusBadge = () => {
    if (syncConfig?.voucherRunning) {
      return (
        <span className="flex items-center gap-1.5 text-xs font-medium text-amber-500">
          <FaPlay className="animate-pulse text-xs" /> Running…
        </span>
      );
    }
    if (!syncConfig?.voucherLastRunStatus || syncConfig.voucherLastRunStatus === 'never') return <span className={`text-xs ${textMuted}`}>Never run</span>;
    return (
      <span className={`text-xs font-medium ${syncConfig.voucherLastRunStatus === 'success' ? 'text-green-500' : 'text-red-500'}`}>
        Last: {syncConfig.voucherLastRunStatus === 'success' ? '✓' : '✗'} {syncConfig.voucherLastRunAt ? new Date(syncConfig.voucherLastRunAt).toLocaleString() : ''}
      </span>
    );
  };

  return (
    <div className="p-6 max-w-3xl">
      <h1 className={`text-2xl font-bold mb-1 ${text}`}>Data Export</h1>
      <p className={`text-sm mb-6 ${textMuted}`}>
        Push selected collections to another MongoDB instance (upsert by _id) — manually or on a 15-minute schedule.
      </p>

      {/* ═══ AUTO SYNC SECTION ═══ */}
      <div className={`rounded-lg shadow mb-6 overflow-hidden ${cardBg}`}>
        <div className={`px-5 py-3 border-b ${divider} flex items-center justify-between`}>
          <div className="flex items-center gap-2">
            <FaSync className={`text-blue-500 ${syncConfig?.enabled ? 'animate-spin' : ''}`} style={syncConfig?.enabled ? { animationDuration: '3s' } : {}} />
            <span className={`font-semibold text-sm ${textLabel}`}>Auto Sync</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
              !syncConfig ? `${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}` :
              syncConfig.enabled ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
            }`}>
              {!syncConfig ? 'Not configured' : syncConfig.enabled ? 'Active — every 15 min' : 'Paused'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {syncStatusBadge()}
          </div>
        </div>

        <div className="p-5 space-y-4">
          {/* URL */}
          <div>
            <label className={`block text-sm font-semibold mb-1.5 ${textLabel}`}>Target MongoDB URL</label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={syncUrl}
                onChange={e => { setSyncUrl(e.target.value); setSyncTestStatus(null); setSyncTestMessage(''); }}
                placeholder="mongodb+srv://user:password@cluster.mongodb.net/dbname"
                className={`flex-1 border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 ${inputCls}`}
              />
              <button
                onClick={handleTestSyncConnection}
                disabled={syncTestStatus === 'testing'}
                className="px-3 py-2 bg-gray-700 text-white text-sm rounded hover:bg-gray-600 disabled:opacity-50 whitespace-nowrap"
              >Test</button>
            </div>
            {syncTestStatus === 'testing' && <span className="text-xs text-yellow-500 mt-1 block">Testing...</span>}
            {syncTestStatus === 'success' && <span className="text-xs text-green-500 mt-1 block">✓ {syncTestMessage}</span>}
            {syncTestStatus === 'error' && <span className="text-xs text-red-500 mt-1 block">✗ {syncTestMessage}</span>}
          </div>

          {/* Collections */}
          <div>
            <label className={`block text-sm font-semibold mb-2 ${textLabel}`}>Collections</label>
            <div className="grid grid-cols-2 gap-2">
              {MODELS.map(m => (
                <label key={m.key} className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={syncModels.includes(m.key)} onChange={() => handleSyncModelToggle(m.key)} className="w-4 h-4 text-blue-600 rounded" />
                  <span className={`text-sm ${text}`}>{m.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={handleSaveSyncConfig}
              disabled={savingSyncConfig || !syncUrl.trim()}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 disabled:opacity-50 transition"
            >
              {savingSyncConfig ? 'Saving...' : syncConfig ? 'Update Config' : 'Enable Auto Sync'}
            </button>

            {syncConfig && (
              <>
                <button
                  onClick={handleToggleSync}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded transition ${
                    syncConfig.enabled
                      ? 'bg-yellow-500 hover:bg-yellow-600 text-white'
                      : 'bg-green-600 hover:bg-green-700 text-white'
                  }`}
                >
                  {syncConfig.enabled ? <><FaToggleOff /> Pause</> : <><FaToggleOn /> Resume</>}
                </button>
                <button
                  onClick={handleRunNow}
                  disabled={runningNow}
                  className="flex items-center gap-1.5 px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white text-sm font-medium rounded disabled:opacity-50 transition"
                >
                  <FaPlay /> {runningNow ? 'Starting...' : 'Run Now'}
                </button>
                <button
                  onClick={handleShowLogs}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded transition ${
                    darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                >
                  <FaHistory /> {showLogs ? 'Hide Logs' : 'Logs'}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Sync logs */}
        {showLogs && (
          <div className={`border-t ${divider}`}>
            {loadingLogs ? (
              <p className={`px-5 py-4 text-sm ${textMuted}`}>Loading logs...</p>
            ) : syncLogs.length === 0 ? (
              <p className={`px-5 py-4 text-sm ${textMuted}`}>No sync logs yet.</p>
            ) : (
              <div className="max-h-72 overflow-y-auto">
                {syncLogs.map((log, i) => (
                  <div key={i} className={`px-5 py-3 border-b ${divider} last:border-0`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-xs font-medium ${log.status === 'success' ? 'text-green-500' : 'text-red-500'}`}>
                        {log.status === 'success' ? '✓ Success' : '✗ Error'}
                      </span>
                      <span className={`text-xs ${textMuted}`}>{new Date(log.runAt).toLocaleString()}</span>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      {log.results?.map((r, j) => (
                        <span key={j} className={`text-xs ${textMuted}`}>
                          {MODELS.find(m => m.key === r.model)?.label || r.model}: {r.upserted}/{r.total}
                          {r.errors > 0 && <span className="text-red-400 ml-1">({r.errorMessage})</span>}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ═══ TALLY VOUCHER DAILY SYNC SECTION ═══ */}
      <div className={`rounded-lg shadow mb-6 overflow-hidden ${cardBg}`}>
        <div className={`px-5 py-3 border-b ${divider} flex items-center justify-between`}>
          <div className="flex items-center gap-2">
            <FaFileInvoiceDollar className={`text-blue-500 ${voucherEnabled ? 'animate-pulse' : ''}`} />
            <span className={`font-semibold text-sm ${textLabel}`}>Tally Vouchers — Daily Sync</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
              !syncConfig?.mongoUrl ? `${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}` :
              !voucherSyncTime ? `${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}` :
              voucherEnabled ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
            }`}>
              {!syncConfig?.mongoUrl ? 'No target DB configured' : !voucherSyncTime ? 'No time set' : voucherEnabled ? `Active — daily at ${voucherSyncTime}` : 'Paused'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {voucherStatusBadge()}
          </div>
        </div>

        <div className="p-5 space-y-4">
          {!syncConfig?.mongoUrl ? (
            <p className={`text-sm ${textMuted}`}>Save the Auto Sync config (MongoDB URL) above first — the voucher export reuses the same target database.</p>
          ) : (
            <>
              {/* Daily time */}
              <div>
                <label className={`block text-sm font-semibold mb-1.5 ${textLabel}`}>Daily Sync Time</label>
                <div className="flex items-center gap-2">
                  <input
                    type="time"
                    value={voucherSyncTime}
                    onChange={e => setVoucherSyncTime(e.target.value)}
                    className={`flex-1 border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 ${inputCls}`}
                  />
                  <button
                    onClick={handleSaveVoucherConfig}
                    disabled={savingVoucherConfig || !voucherSyncTime}
                    className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 disabled:opacity-50 transition whitespace-nowrap"
                  >
                    {savingVoucherConfig ? 'Saving...' : 'Save Time'}
                  </button>
                </div>
                <p className={`text-xs mt-1.5 ${textMuted}`}>
                  Exports Tally Vouchers from all companies to the target database. The first run populates everything;
                  later runs export only vouchers created or updated since the last run (incremental by updatedAt).
                </p>
              </div>

              {/* Action buttons */}
              <div className="flex items-center gap-2 pt-1">
                <button
                  onClick={handleToggleVoucherSync}
                  disabled={!voucherSyncTime}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded transition disabled:opacity-50 ${
                    voucherEnabled
                      ? 'bg-yellow-500 hover:bg-yellow-600 text-white'
                      : 'bg-green-600 hover:bg-green-700 text-white'
                  }`}
                >
                  {voucherEnabled ? <><FaToggleOff /> Pause</> : <><FaToggleOn /> Enable Daily Sync</>}
                </button>
                <button
                  onClick={() => handleVoucherRunNow(false)}
                  disabled={runningVoucherNow || syncConfig?.voucherRunning}
                  className="flex items-center gap-1.5 px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white text-sm font-medium rounded disabled:opacity-50 transition"
                >
                  <FaPlay /> {syncConfig?.voucherRunning ? 'Running…' : 'Run Incremental'}
                </button>
                <button
                  onClick={() => handleVoucherRunNow(true)}
                  disabled={runningVoucherNow || syncConfig?.voucherRunning}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded transition disabled:opacity-50 ${
                    darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                >
                  <FaPlay /> {syncConfig?.voucherRunning ? 'Running…' : 'Run Full'}
                </button>
                <button
                  onClick={handleShowVoucherLogs}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded transition ${
                    darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                >
                  <FaHistory /> {showVoucherLogs ? 'Hide Logs' : 'Logs'}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Voucher sync logs */}
        {showVoucherLogs && (
          <div className={`border-t ${divider}`}>
            {loadingVoucherLogs ? (
              <p className={`px-5 py-4 text-sm ${textMuted}`}>Loading logs...</p>
            ) : voucherLogs.length === 0 ? (
              <p className={`px-5 py-4 text-sm ${textMuted}`}>No voucher sync logs yet.</p>
            ) : (
              <div className="max-h-72 overflow-y-auto">
                {voucherLogs.map((log, i) => (
                  <div key={i} className={`px-5 py-3 border-b ${divider} last:border-0`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-medium ${log.status === 'success' ? 'text-green-500' : 'text-red-500'}`}>
                          {log.status === 'success' ? '✓ Success' : '✗ Error'}
                        </span>
                        {log.message && <span className={`text-xs ${textMuted}`}>{log.message}</span>}
                      </div>
                      <span className={`text-xs ${textMuted}`}>{new Date(log.runAt).toLocaleString()}</span>
                    </div>
                    {log.results?.length > 0 && (
                      <div className="flex flex-wrap gap-3 mt-1">
                        {log.results.map((r, j) => (
                          <span key={j} className={`text-xs ${textMuted}`}>
                            {r.company}: {r.upserted}/{r.total}
                            {r.errors > 0 && <span className="text-red-400 ml-1">({r.errorMessage})</span>}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ═══ TALLY ITEMS & STOCK GROUPS DAILY SYNC (selected company) ═══ */}
      <CatalogExportCard
        title="Tally Items — Daily Sync"
        icon={<FaBoxOpen />}
        model="items"
        prefix="item"
        label="Tally Items"
        darkMode={darkMode}
      />
      <CatalogExportCard
        title="Tally Stock Groups — Daily Sync"
        icon={<FaSitemap />}
        model="stockGroups"
        prefix="stockGroup"
        label="Tally Stock Groups"
        darkMode={darkMode}
      />

      {/* ═══ MANUAL EXPORT SECTION ═══ */}
      <div className={`rounded-lg shadow mb-5 overflow-hidden ${cardBg}`}>
        <div className={`px-5 py-3 border-b ${divider}`}>
          <span className={`font-semibold text-sm ${textLabel}`}>Manual One-Time Export</span>
        </div>
        <div className="p-5 space-y-5">
          {/* MongoDB URL */}
          <div>
            <label className={`block text-sm font-semibold mb-1.5 ${textLabel}`}>Target MongoDB URL</label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={mongoUrl}
                onChange={e => { setMongoUrl(e.target.value); setConnectionStatus(null); setConnectionMessage(''); }}
                placeholder="mongodb+srv://user:password@cluster.mongodb.net/dbname"
                className={`flex-1 border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 ${inputCls}`}
              />
              <button
                onClick={handleTestConnection}
                disabled={connectionStatus === 'testing'}
                className="px-3 py-2 bg-gray-700 text-white text-sm rounded hover:bg-gray-600 disabled:opacity-50 whitespace-nowrap"
              >Test Connection</button>
            </div>
            {connectionBadge()}
          </div>

          {/* Model Selection */}
          <div>
            <label className={`block text-sm font-semibold mb-2 ${textLabel}`}>Select Collections to Export</label>
            <div className="grid grid-cols-2 gap-2">
              {MODELS.map(m => (
                <label key={m.key} className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={selectedModels.includes(m.key)} onChange={() => handleModelToggle(m.key)} className="w-4 h-4 text-blue-600 rounded" />
                  <span className={`text-sm ${text}`}>{m.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Date Range */}
          <div>
            <label className={`block text-sm font-semibold mb-1 ${textLabel}`}>Date Range <span className={`font-normal ${textMuted}`}>(optional — filters by createdAt)</span></label>
            <div className="flex items-center gap-4">
              <div className="flex flex-col flex-1">
                <label className={`text-xs mb-1 ${textMuted}`}>From</label>
                <input type="date" value={dateRange.from} onChange={e => setDateRange(prev => ({ ...prev, from: e.target.value }))} className={`border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 ${inputCls}`} />
              </div>
              <div className="flex flex-col flex-1">
                <label className={`text-xs mb-1 ${textMuted}`}>To</label>
                <input type="date" value={dateRange.to} onChange={e => setDateRange(prev => ({ ...prev, to: e.target.value }))} className={`border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 ${inputCls}`} />
              </div>
            </div>
          </div>

          <button
            onClick={handleExport}
            disabled={exporting || connectionStatus !== 'success' || selectedModels.length === 0}
            className="w-full py-2.5 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {exporting ? 'Exporting...' : 'Export to Target Database'}
          </button>
        </div>
      </div>

      {/* Results */}
      {results && (
        <div className={`rounded-lg shadow overflow-hidden ${cardBg}`}>
          <div className={`px-5 py-3 border-b ${divider}`}>
            <h2 className={`text-sm font-semibold ${textLabel}`}>Export Results</h2>
          </div>
          <table className="min-w-full">
            <thead className={theadBg}>
              <tr>
                <th className={`px-5 py-3 text-left text-xs font-medium uppercase ${theadText}`}>Collection</th>
                <th className={`px-5 py-3 text-right text-xs font-medium uppercase ${theadText}`}>Total Found</th>
                <th className={`px-5 py-3 text-right text-xs font-medium uppercase ${theadText}`}>Upserted</th>
                <th className={`px-5 py-3 text-right text-xs font-medium uppercase ${theadText}`}>Status</th>
              </tr>
            </thead>
            <tbody className={`${tbodyBg} divide-y ${tbodyDivide}`}>
              {results.map((r, i) => (
                <tr key={i}>
                  <td className={`px-5 py-3 text-sm font-medium ${tbodyText}`}>
                    {MODELS.find(m => m.key === r.model)?.label || r.model}
                    {r.collection && <span className={`ml-1 text-xs ${tbodyMuted}`}>({r.collection})</span>}
                  </td>
                  <td className={`px-5 py-3 text-sm text-right ${tbodyMuted}`}>{r.total}</td>
                  <td className="px-5 py-3 text-sm text-right text-green-500 font-medium">{r.upserted}</td>
                  <td className="px-5 py-3 text-sm text-right">
                    {r.errors > 0
                      ? <span className="text-red-500 font-medium" title={r.errorMessage}>✗ Error</span>
                      : <span className="text-green-500 font-medium">✓ OK</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DataExport;

